import { useEffect, useRef, useState, type FocusEvent, type KeyboardEvent } from 'react';
import { Search, X } from 'lucide-react';
import {
  cn,
  Input,
  Button,
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@clarix/ui';

interface SearchInputProps {
  value: string;
  onChange: (value: string) => void;
  placeholder?: string;
  className?: string;
  inputClassName?: string;
  /** Compact toolbar size (`h-6`, `text-lg`) vs the default field (`h-10`). */
  size?: 'sm' | 'default';
  /**
   * Icon-only trigger that expands into a field on click (and collapses again
   * when blurred while empty). Used by toolbars where space is tight — e.g. the
   * project Tasks tab and Timesheet Reports.
   */
  expandable?: boolean;
  /** Width of the field when expanded (only used with `expandable`). */
  expandedWidthClassName?: string;
  /** Tooltip shown on hover over the collapsed search icon (`expandable` mode). */
  tooltip?: string;
  /**
   * Debounce (ms) before `onChange` fires while typing. The displayed text
   * updates instantly; `onChange` (which usually triggers a refetch) is
   * deferred. Clearing via the ✕ flushes immediately. Pass `0` to disable
   * (e.g. when the call site already debounces).
   */
  debounce?: number;
  'aria-label'?: string;
  /** Focus the field on mount (default mode only). */
  autoFocus?: boolean;
  /** Forwarded to the input (default mode only) — e.g. blur-to-close overlays. */
  onBlur?: (e: FocusEvent<HTMLInputElement>) => void;
  /** Forwarded to the input (default mode only) — e.g. Escape-to-close overlays. */
  onKeyDown?: (e: KeyboardEvent<HTMLInputElement>) => void;
}

/**
 * The single canonical search field used across every app/page.
 *
 * The focus state is one soft, token-driven border highlight — the ring hugs
 * the border (no offset) so it reads as a SINGLE slightly-stronger border
 * rather than a border plus a detached outline, at a reduced opacity. The
 * highlight sits on the container in `expandable` mode and on the input itself
 * in the default mode, so both modes look identical when focused.
 *
 * Typing is debounced internally (see `debounce`) so every consumer gets
 * debounced search without wiring its own timer.
 */
export function SearchInput({
  value,
  onChange,
  placeholder = 'Search...',
  className,
  inputClassName,
  size = 'default',
  expandable = false,
  expandedWidthClassName = 'w-48',
  tooltip,
  debounce = 300,
  'aria-label': ariaLabel,
  autoFocus,
  onBlur,
  onKeyDown,
}: SearchInputProps) {
  const compact = size === 'sm';
  const label = ariaLabel ?? placeholder;
  const tooltipLabel = tooltip ?? 'Search';
  const inputRef = useRef<HTMLInputElement>(null);
  const [focused, setFocused] = useState(false);

  // `inner` is the instantly-updating text shown in the field; `onChange` is
  // emitted from it on a debounce so the parent (and any refetch) only sees
  // the settled value. `lastEmitted` tracks the value last pushed to / pulled
  // from the parent so neither the debounce nor the external-sync effect echo.
  const [inner, setInner] = useState(value);
  const lastEmitted = useRef(value);
  const onChangeRef = useRef(onChange);
  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  // Sync when the parent changes `value` from the outside (e.g. clearing
  // filters from another surface) — not when it merely echoes our own emit.
  useEffect(() => {
    if (value !== lastEmitted.current) {
      lastEmitted.current = value;
      setInner(value);
    }
  }, [value]);

  // Debounced emit of the typed value.
  useEffect(() => {
    if (inner === lastEmitted.current) return;
    if (debounce <= 0) {
      lastEmitted.current = inner;
      onChangeRef.current(inner);
      return;
    }
    const t = setTimeout(() => {
      lastEmitted.current = inner;
      onChangeRef.current(inner);
    }, debounce);
    return () => clearTimeout(t);
  }, [inner, debounce]);

  // Commit immediately, bypassing the debounce (used by the clear button).
  const commitNow = (next: string) => {
    lastEmitted.current = next;
    setInner(next);
    onChangeRef.current(next);
  };

  // Single, softened border highlight on focus: the ring hugs the border (no
  // offset) at a reduced opacity — shared by every page and both modes.
  const focusHighlight =
    'focus-visible:ring-1 focus-visible:ring-offset-0 focus-visible:ring-ring/40 focus-visible:border-ring/60';

  if (expandable) {
    const open = focused || inner.length > 0;

    return (
      <div
        className={cn(
          'bg-surface flex items-center overflow-hidden rounded-md border transition-[width,border-color,box-shadow]',
          open
            ? cn(
                expandedWidthClassName,
                'border-border focus-within:ring-1 focus-within:ring-offset-0 focus-within:ring-ring/40 focus-within:border-ring/60',
              )
            : 'w-8 border-transparent',
          className,
        )}
      >
        {open ? (
          <>
            <Search
              className="text-muted-foreground ml-2 h-3.5 w-3.5 shrink-0"
              strokeWidth={1.5}
              aria-hidden
            />
            <Input
              ref={inputRef}
              type="text"
              value={inner}
              onChange={(e) => setInner(e.target.value)}
              onFocus={() => setFocused(true)}
              onBlur={() => setFocused(false)}
              placeholder={placeholder}
              aria-label={label}
              autoFocus
              className="h-7 min-w-0 flex-1 rounded-none border-0 bg-transparent px-2 text-lg focus-visible:ring-0"
            />
            {inner && (
              <Button
                variant="ghost"
                size="icon"
                aria-label="Clear search"
                className="mr-0.5 h-6 w-6 shrink-0"
                onClick={() => commitNow('')}
              >
                <X className="h-3.5 w-3.5" strokeWidth={1.5} />
              </Button>
            )}
          </>
        ) : (
          <TooltipProvider>
            <Tooltip delayDuration={300}>
              <TooltipTrigger asChild>
                <button
                  type="button"
                  aria-label={label}
                  onClick={() => {
                    setFocused(true);
                    requestAnimationFrame(() => inputRef.current?.focus());
                  }}
                  className="text-muted-foreground hover:text-foreground flex h-7 w-7 items-center justify-center"
                >
                  <Search className="h-3.5 w-3.5" strokeWidth={1.5} />
                </button>
              </TooltipTrigger>
              <TooltipContent side="top" sideOffset={4}>
                {tooltipLabel}
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    );
  }

  return (
    <div className={cn('relative', className)}>
      <Search
        className={cn(
          'text-muted-foreground absolute top-1/2 -translate-y-1/2',
          // Compact toolbar search: 12px icon inset 8px (for the h-6 field).
          compact
            ? 'left-2 h-3 w-3'
            : 'left-3 h-4 w-4',
        )}
        strokeWidth={1.5}
        aria-hidden
      />
      <Input
        type="text"
        value={inner}
        onChange={(e) => setInner(e.target.value)}
        placeholder={placeholder}
        aria-label={label}
        autoFocus={autoFocus}
        onBlur={onBlur}
        onKeyDown={onKeyDown}
        className={cn(
          focusHighlight,
          // `md:text-sm` re-asserts the size at md+: the base Input carries
          // `md:text-xl` (14px), which would otherwise win over an unprefixed
          // size. Compact toolbar search = h-6 with 11px text + 12px icons.
          compact
            ? 'h-6 pl-6 pr-7 text-sm md:text-sm'
            : 'pl-9 pr-9',
          inputClassName,
        )}
      />
      {inner && (
        <Button
          variant="ghost"
          size="icon"
          aria-label="Clear search"
          className={cn(
            'absolute top-1/2 -translate-y-1/2',
            compact
              ? 'right-0.5 h-5 w-5'
              : 'right-1 h-7 w-7',
          )}
          onClick={() => commitNow('')}
        >
          <X
            className={cn(
              compact ? 'h-3 w-3' : 'h-4 w-4',
            )}
            strokeWidth={1.5}
          />
        </Button>
      )}
    </div>
  );
}
